import Lenis from 'lenis'
import { Metrics } from '../core/Metrics';
import { isReducedMotion, isSmartphone, isTouch } from '../core/Basics';
import Scroll_Item from './Scroll_Item';
import { Functions } from '../utils/Functions';

export class Scroll {
  static isActive = false;
  /** Intención de permitir scroll (válida también antes de init). */
  static _wantEnabled = true;
  static lenis = null;
  static rafId = null;
  static _contentRO = null;
  static _contentROTimer = null;

  static defaultOptions = {
    autoRaf: false,
    smooth: true,
    lerp: 0.1,
  };

  static observerOptions = {
    rootMargin: '0px 0px 0px 0px',
    threshold: 0,
  };

  static total_items = 0;
  static _items = [];
  static _itemsAuto = [];
  static _itemsObserver = {};
  static _classItems = [];
  static _insidersItems = [];

  static set y(n) { this.lenis?.scrollTo(n); }
  static get y() { return this.lenis?.animatedScroll ?? 0; }
  static get limit() { return this.lenis?.limit; }
  static get progress() { return this.lenis?.progress; }
  static get direction() { return this.lenis?.direction; }
  static get actualScroll() { return this.lenis?.actualScroll; }
  static get speed() { return this.lenis?.velocity; }
  static get lastSpeed() { return this.lenis?.lastVelocity; }
  static get isScrolling() { return this.lenis?.isScrolling; }

  /** sessionStorage: restaura scroll al refrescar (misma URL). */
  static _SCROLL_STORE = 'gf_scroll_y';
  static _persistTimer = null;
  static _onPageHide = null;

  static _persistKey() {
    return `${window.location.pathname}${window.location.search}`;
  }

  static persistY() {
    try {
      sessionStorage.setItem(this._SCROLL_STORE, JSON.stringify({
        key: this._persistKey(),
        y: this.y || 0,
      }));
    } catch (_) { /* private mode / quota */ }
  }

  static readPersistedY() {
    try {
      const raw = sessionStorage.getItem(this._SCROLL_STORE);
      if (!raw) return 0;
      const data = JSON.parse(raw);
      if (data.key !== this._persistKey()) return 0;
      return typeof data.y === 'number' && data.y > 0 ? data.y : 0;
    } catch (_) {
      return 0;
    }
  }

  static clearPersistedY() {
    try { sessionStorage.removeItem(this._SCROLL_STORE); } catch (_) { /* */ }
  }

  static _schedulePersistY() {
    clearTimeout(this._persistTimer);
    this._persistTimer = setTimeout(() => this.persistY(), 150);
  }

  static _bindPersist() {
    this._unbindPersist();
    this._onPageHide = () => this.persistY();
    window.addEventListener('pagehide', this._onPageHide);
  }

  static _unbindPersist() {
    clearTimeout(this._persistTimer);
    this._persistTimer = null;
    if (this._onPageHide) {
      window.removeEventListener('pagehide', this._onPageHide);
      this._onPageHide = null;
    }
  }

  /**
   * @param {object} options Lenis options. Extra: `startY` (number) — scroll inicial (default 0).
   * @param {boolean} isStopped Si true, Lenis arranca parado.
   */
  static init(options = {}, isStopped = false) {
    // Manual: el navegador no ha de restaurar el scroll (desquadra getBoundingClientRect + Scroll.y).
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    const { startY = 0, ...lenisOptions } = options;

    if (!this.lenis) {
      this.lenis = new Lenis({ ...this.defaultOptions, ...lenisOptions });
      this.lenis.on('scroll', () => {
        this.updateItems();
        this._schedulePersistY();
      });
      this._setupContentResize();
    }

    const y = typeof startY === 'number' && startY > 0 ? startY : 0;
    this.lenis.scrollTo(y, { immediate: true });
    // Respeta disable() hecho ANTES de init, o isStopped en la llamada
    if (isStopped || !this._wantEnabled) this.lenis.stop();
    else this.lenis.start();

    this.setupObserver();
    this._bindPersist();

    setTimeout(() => {
      this._addAll();
      this.resize();
      // Reaplica por si el layout cambió el limit tras medir items
      if (y > 0) this.lenis.scrollTo(y, { immediate: true });
      this.persistY();
    }, 100);

    // Fonts: poden canviar l'altura del text sense disparar el RO de Lenis (html height:100%).
    document.fonts?.ready?.then(() => {
      if (this.isActive) this.resize();
    });

    this.isActive = true;
  }

  // html té height:100% → el ResizeObserver intern de Lenis (sobre documentElement) NO dispara
  // quan creix el contingut (imgs, vídeo, SplitText…). Observem #Main (creix amb el contingut)
  // i forcem lenis.resize() + limits dels items.
  static _setupContentResize() {
    this._contentRO?.disconnect();
    this._contentRO = null;

    const main = document.getElementById('Main');
    if (!main || typeof ResizeObserver === 'undefined') return;

    this._contentRO = new ResizeObserver(() => {
      clearTimeout(this._contentROTimer);
      this._contentROTimer = setTimeout(() => {
        if (this.lenis) this.resize();
      }, 50);
    });
    this._contentRO.observe(main);
  }

  static _registerClass(id, klass) {
    this._classItems.push({ id, class: klass });
  }

  static _getClass(item) {
    const idClass = item.getAttribute('data-class') || 'default';

    for (const entry of this._classItems) {
      if (entry.id === idClass) return entry.class;
    }

    if (item.getAttribute('data-class')) {
      console.warn(`scroll-item [${idClass}] no existe, posiblemente no hayas hecho el import`);
    }

    return Scroll_Item;
  }

  static _getClassVS(__item, __default) {
    let idClass = __item.getAttribute('data-class') || 'default';

    for (let i = 0, j = Scroll._classItems.length; i < j; i++) {
      if (idClass === Scroll._classItems[i].id) {
        return Scroll._classItems[i].class;
      }
    }

    if (__item.getAttribute('data-class')) {
      console.warn(`scroll-item [${__item.getAttribute('data-class')}] no existe, posiblemente no hayas hecho el import`);
    }

    return __default || VScroll_Item;
  }

  static _registerInsider(insider) {
    this._insidersItems.push({ id: insider.selector, class: insider });
  }

  static setScrollbar(scrollbar) { }

  static setupObserver() {
    if (this._observer) this._observer.disconnect();

    if (!isReducedMotion) {
      this._observer = new IntersectionObserver(
        entries => this.checkObserver(entries),
        this.observerOptions
      );
    }
  }

  static checkObserver(entries) {
    for (const entry of entries) {
      const id = Functions.getId(entry.target);
      const item = this._itemsObserver[id];
      entry.isIntersecting ? item.show() : item.hide();
    }
  }

  static start() {
    this.enable();
  }

  static stop() {
    this.disable();
  }

  /** Permite scroll de usuario (wheel / touch / keys vía Lenis). */
  static enable() {
    this._wantEnabled = true;
    this.lenis?.start();
  }

  /** Bloquea la capacidad de hacer scroll (p. ej. menú, modal, transición). */
  static disable() {
    this._wantEnabled = false;
    this.lenis?.stop();
  }

  /** true si el usuario puede hacer scroll. */
  static get isEnabled() {
    return this._wantEnabled && !!this.lenis && !this.lenis.isStopped;
  }

  static set isEnabled(value) {
    value ? this.enable() : this.disable();
  }

  static loop(time) {
    if (this.lenis) {
      this.lenis.raf(time * 1000);
    }
  }

  static updateItems() {
    for (let i = 0; i < this.total_items; i++) {
      this._items[i].update();
    }
  }

  static resize() {
    this.lenis?.resize();

    this._items.forEach(item => {
      item.resize();
      item.resizeLimits(Metrics.WIDTH, Metrics.HEIGHT);
    });
  }

  static show() { }
  static hide() { }

  // Scroll navigation
  static goto(n) {
    this.lenis.scrollTo(n);
  }

  static directGoto(n) {
    this.lenis.scrollTo(n, { offset: -Metrics.OFFSET, immediate: true });
  }

  static gotoDOMElement(el) {
    this.lenis.scrollTo(el, { offset: -Metrics.OFFSET });
  }

  static gotoID(id) {
    if (!this.lenis || !id || !document.getElementById(id)) return;
    this.lenis.scrollTo(`#${id}`, { offset: -(Metrics.OFFSET || 0) });
  }

  static gotoAnchor(anchor) {
    this.gotoID(anchor.startsWith('#') ? anchor.substring(1) : anchor);
  }

  /** Si la URL tiene hash (#id), hace scroll al ancla cuando Lenis ya está listo. */
  static gotoHash({ immediate = false, delay = 150 } = {}) {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;

    const id = hash.substring(1);
    if (!document.getElementById(id)) return;

    const go = () => {
      if (!this.lenis) return;
      this.lenis.scrollTo(`#${id}`, {
        offset: -(Metrics.OFFSET || 0),
        immediate,
      });
    };

    // Scroll.init añade items a los 100ms; esperamos un poco más para posiciones correctas
    if (delay > 0) setTimeout(go, delay);
    else go();
  }

  static gotoPercentage(n) {
    this.lenis.scrollTo(this.lenis.limit * n, { offset: -Metrics.OFFSET });
  }

  static _addAll(selector = "[scroll-item]") {
    const items = document.querySelectorAll(selector);

    for (const item of items) {
      item.setAttribute("scroll-item-loaded", "");
      item.removeAttribute("scroll-item");

      const allowMobile = isTouch ? item.getAttribute("data-avoid-mobile") === null : true;
      const allowSmartphone = isSmartphone ? item.getAttribute("data-avoid-smartphone") === null : true;
      const allowMove = item.getAttribute("data-scroll-auto") === null;

      if (allowMobile && allowSmartphone) {
        const Class = this._classItems.length > 0 ? this._getClass(item) : Scroll_Item;
        const instance = new Class(item, this.total_items, this);

        if (allowMove)
          this.total_items = this._items.push(instance);
        else
          this._itemsAuto.push(instance);

        this._itemsObserver[instance.id] = instance;

        if (!isReducedMotion) {
          this._observer.observe(instance.item);
        }
      }
    }
  }

  static dispose() {
    this.persistY();
    this._unbindPersist();

    clearTimeout(this._contentROTimer);
    this._contentRO?.disconnect();
    this._contentRO = null;

    this.lenis?.destroy();
    this.lenis = null;

    this._observer?.disconnect();
    this._observer = null;

    for (const item of this._items) { item.dispose(); }
    for (const item of this._itemsAuto) { item.dispose(); }

    this._items = [];
    this._itemsAuto = [];
    this.total_items = 0;
    this._itemsObserver = {};
    this.isActive = false;
    this._wantEnabled = true;
  }
}
